import { PerDiem, PerDiemDailyExpenses, PerDiemDestination } from "@finway-group/shared/lib/models"
import { roundNumberTo2Decimals } from "Shared/utils/helpers"
import moment, { unitOfTime } from "moment"
import { FormPerDiemDestinationItinerary, PerDiemDestinationItinerary } from "./perDiem.form"
import { PER_DIEM_BREAKFAST_RATE, PER_DIEM_LUNCH_RATE, PER_DIEM_DINNER_RATE, DEFAULT_PER_DIEM_DESTINATION, REIMBURSABLE_DURATION_THRESHOLD_MINUTE } from "Shared/config/consts"

export const generatePerDiemDestinationLabel = (p: PerDiemDestination) => {
    if (p.country) {
        return `${p.destination}, ${p.country}`
    }
    return p.destination
}

export const calculateDayReimbursement = (day: PerDiemDailyExpenses, tripDailyExpenses: PerDiemDailyExpenses[], index: number, destination: PerDiemDestination) => {
    const isLastDay = index === tripDailyExpenses.length - 1
    const mealAllowance = (index === 0 || index === tripDailyExpenses.length - 1 ? destination?.halfDay : destination?.fullDay) ?? 0
    const reimbursement = destination?.fullDay ?? 0

    let substractableAmountByMeals = 0
    if (day?.expenditure?.breakfast) substractableAmountByMeals += (reimbursement || 0) * PER_DIEM_BREAKFAST_RATE
    if (day?.expenditure?.lunch) substractableAmountByMeals += (reimbursement || 0) * PER_DIEM_LUNCH_RATE
    if (day?.expenditure?.dinner) substractableAmountByMeals += (reimbursement || 0) * PER_DIEM_DINNER_RATE

    // Substractable amount can never be higher than total meal allowance.
    let totalReimbursement = substractableAmountByMeals > mealAllowance ? 0 : mealAllowance - substractableAmountByMeals

    // If accomodation is not checked, it gives +value, if its checked, it gives 0
    if (!day?.expenditure?.accommodation && !isLastDay) totalReimbursement += destination?.accommodation || 0

    return roundNumberTo2Decimals(totalReimbursement)
}

export const calculateTotalReimbursement = (tripDailyExpenses: PerDiemDailyExpenses[], destinations: Map<string, PerDiemDestination>) => {
    let totalValue = 0
    for (const [index, day] of tripDailyExpenses.entries()) {
        const destination = destinations.get(day.destination)!
        totalValue += calculateDayReimbursement(day, tripDailyExpenses, index, destination)
    }
    return totalValue
}

const getFirstAndLastDestinations = (itinerary: PerDiemDestinationItinerary[]) => {
    if (!itinerary || itinerary.length === 0) {
        return undefined
    }
    return [itinerary[0], itinerary[itinerary.length - 1]]
}

const getPerDiemTravelDuration = (start: PerDiemDestinationItinerary, end: PerDiemDestinationItinerary, unit: unitOfTime.Diff = "hour") => {
    return moment(end.endDate).diff(start.startDate, unit)
}

export const isPerDiemTripNotReimbursable = (perDiemExpense: PerDiem, perDiemDestinations: PerDiemDestination[]) => {
    const destinations = getFirstAndLastDestinations(perDiemExpense.destinations)
    if (!destinations) return false
    const [start, end] = destinations
    if (!start || !end) {
        return false
    }

    const deutschlandDestination = perDiemDestinations.find((d) => d.destination === DEFAULT_PER_DIEM_DESTINATION)

    // For trips in germany, trips less or equal than 8 minutes cannot be reimbursed. (8 min + 1 sec can though)
    const minute = getPerDiemTravelDuration(start, end, "minute")
    return end.destination === deutschlandDestination?._id && minute <= REIMBURSABLE_DURATION_THRESHOLD_MINUTE
}

export const formatPerDiemDestinationDates = (expense: PerDiem): FormPerDiemDestinationItinerary[] => {
    return expense.destinations.map((d) => ({
        ...d,
        startDate: moment(d?.startDate),
        endDate: moment(d?.endDate),
    }))
}
